import React, { useMemo } from "react";

// ============================================================
// FMS completo según planilla ISEF N°1 Dr. E. Romero Brest
// 7 pruebas, cada una con sus sub-criterios y fórmulas.
// ============================================================

// Tabla de posición inicial (VARONES solo)
const TRUNK_POSITION = [
  { value: "Frente", label: "Frente", points: 3 },
  { value: "Mentón", label: "Mentón", points: 2 },
  { value: "Clavículas", label: "Clavículas", points: 1 },
];

// Tabla de distancia de hombros
const SHOULDER_DISTANCE = [
  { value: "menor_1", label: "distancia MENOR A 1 mano", points: 3 },
  { value: "igual_1_5", label: "distancia IGUAL A 1,5 mano", points: 2 },
  { value: "mayor_1_5", label: "distancia MAYOR a 1,5 mano", points: 1 },
];

// Tabla Active SLR
const SLR_OPTIONS = [
  { value: "supera_muslo", label: "Maléolo supera mitad del muslo", points: 3 },
  { value: "supera_rodilla", label: "Maléolo supera solo la rodilla", points: 2 },
  { value: "no_supera", label: "Maléolo no supera rodilla", points: 1 },
];

// ============================================================
// Puntaje por prueba (según fórmulas Excel)
// ============================================================

// Deep Squat (row 5-11, single side) — 6 criterios, 3 directos + 3 invertidos (triples negativos)
// Criterios (true=1 salvo los que indico):
const DEEP_SQUAT_CRITERIA = [
  { key: "tronco_paralelo_tibias", label: "Línea Tronco paralela a Tibias", inverted: false },
  { key: "femur_horizontal", label: "Fémur debajo horizontal", inverted: false },
  { key: "rodillas_pies", label: "Línea Rodillas con pies", inverted: false },
  { key: "barra_pies", label: "Línea vertical Barra con pies", inverted: false },
  { key: "eleva_talones", label: "¿Eleva talones?", inverted: true }, // false = 1
  { key: "flexion_lumbar_ds", label: "¿Flexión lumbar?", inverted: true }, // false = 1
];
function scoreDeepSquat(criteria) {
  const sum = DEEP_SQUAT_CRITERIA.reduce((acc, c) => {
    const v = criteria?.[c.key];
    if (c.inverted) return acc + (v === false ? 1 : 0);
    return acc + (v === true ? 1 : 0);
  }, 0);
  if (sum === 6) return 3;
  if (sum === 5) return 2;
  return 1;
}

// Hurdle Step (row 14-18, L/R) — 4 criterios
const HURDLE_CRITERIA = [
  { key: "art_alineadas", label: "Art. alineadas eje sagital", inverted: false },
  { key: "mov_lumbar", label: "Existe mov. lumbar", inverted: true }, // false = 1
  { key: "barra_paralela", label: "Barra paralela al obstáculo", inverted: false },
  { key: "equilibrio_hurdle", label: "Mantiene el equilibrio", inverted: false },
];
function scoreHurdle(criteria) {
  const sum = HURDLE_CRITERIA.reduce((acc, c) => {
    const v = criteria?.[c.key];
    if (c.inverted) return acc + (v === false ? 1 : 0);
    return acc + (v === true ? 1 : 0);
  }, 0);
  if (sum === 4) return 3;
  if (sum === 3) return 2;
  return 1;
}

// Inline Lunge (row 22-28, L/R) — 6 criterios
const LUNGE_CRITERIA = [
  { key: "contacto_barra", label: "Contacto barra cuerpo", inverted: false },
  { key: "barra_vertical", label: "¿Barra se mantiene vertical?", inverted: false },
  { key: "flexion_tronco", label: "¿Mov. flexión de tronco?", inverted: true }, // false = 1
  { key: "barra_pie_sagital", label: "Barra y pie plano sagital", inverted: false },
  { key: "rodilla_talon", label: "¿La rodilla posterior toca talón?", inverted: false },
  { key: "equilibrio_lunge", label: "¿Mantiene el equilibrio?", inverted: false },
];
function scoreLunge(criteria) {
  const sum = LUNGE_CRITERIA.reduce((acc, c) => {
    const v = criteria?.[c.key];
    if (c.inverted) return acc + (v === false ? 1 : 0);
    return acc + (v === true ? 1 : 0);
  }, 0);
  if (sum === 6) return 3;
  if (sum === 1) return 2; // según fórmula del Excel
  return 1;
}

// Shoulder Mobility — tabla simple, VLOOKUP
function scoreShoulder(option) {
  return SHOULDER_DISTANCE.find((o) => o.value === option)?.points ?? 0;
}

// Active SLR — tabla simple
function scoreSLR(option) {
  return SLR_OPTIONS.find((o) => o.value === option)?.points ?? 0;
}

// Trunk Stability (push up, row 37-41) — MUJER vs VARÓN
// Mujer: 4 criterios (Mentón TRUE=1, Elevación TRUE=1, Compensaciones FALSE=1, Pinzamiento TRUE=10)
//   Puntaje: IF suma>3 then 0, IF 3 then 3, IF 2 then 2, else 1
// Varón: igual pero la posición inicial (Frente=3/Mentón=2/Clavículas=1) en vez de 1/0
//   Puntaje: IF suma>5.5 then 0, IF 5 then 3, IF 4 then 2, else 1
function scoreTrunk(sex, criteria) {
  const isMale = sex === "MASCULINO";
  let sum = 0;
  if (isMale) {
    sum += scoreTrunkPosition(criteria?.posicion);
  } else {
    sum += criteria?.posicion === "Mentón" ? 1 : 0;
  }
  sum += criteria?.elevacion_unidad === true ? 1 : 0;
  sum += criteria?.compensaciones === false ? 1 : 0;
  sum += criteria?.pinzamiento === true ? 10 : 0;
  if (isMale) {
    if (sum > 5.5) return 0;
    if (sum === 5) return 3;
    if (sum === 4) return 2;
    return 1;
  } else {
    if (sum > 3) return 0;
    if (sum === 3) return 3;
    if (sum === 2) return 2;
    return 1;
  }
}
function scoreTrunkPosition(option) {
  return TRUNK_POSITION.find((o) => o.value === option)?.points ?? 0;
}

// Rotary Stability (row 43-45) — sin L/R
const ROTARY_CRITERIA = [
  { key: "unilateral", label: "Realiza repetición unilateral" },
  { key: "diagonal", label: "Realiza repetición diagonal" },
  { key: "dolor_flexion", label: "¿Dolor en flexión tronco?" },
];
function scoreRotary(criteria) {
  if (criteria?.dolor_flexion === true) return 0;
  if (criteria?.unilateral === true) return 3;
  if (criteria?.diagonal === true) return 2;
  return 1;
}

// ============================================================
// Helper: total FMS
// ============================================================
export function computeFmsTotals(data, sex = "MASCULINO") {
  const deepSquat = scoreDeepSquat(data.deep_squat);
  const hurdleL = scoreHurdle(data.hurdle_left);
  const hurdleR = scoreHurdle(data.hurdle_right);
  const hurdle = Math.min(hurdleL, hurdleR);
  const lungeL = scoreLunge(data.lunge_left);
  const lungeR = scoreLunge(data.lunge_right);
  const lunge = Math.min(lungeL, lungeR);
  const shoulderL = scoreShoulder(data.shoulder_left);
  const shoulderR = scoreShoulder(data.shoulder_right);
  const shoulder = Math.min(shoulderL, shoulderR) || 0;
  const slrL = scoreSLR(data.slr_left);
  const slrR = scoreSLR(data.slr_right);
  const slr = Math.min(slrL, slrR) || 0;
  const trunk = scoreTrunk(sex, data.trunk_stability);
  const rotary = scoreRotary(data.rotary);
  const total = deepSquat + hurdle + lunge + shoulder + slr + trunk + rotary;
  return { deepSquat, hurdle, hurdleL, hurdleR, lunge, lungeL, lungeR, shoulder, shoulderL, shoulderR, slr, slrL, slrR, trunk, rotary, total };
}

// ============================================================
// UI primitives
// ============================================================

function YesNoToggle({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
          value === true ? "bg-[#32D74B] text-black border-[#32D74B]" : "border-[#262626] text-muted hover:border-white hover:text-white"
        }`}
      >
        SÍ
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
          value === false ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "border-[#262626] text-muted hover:border-white hover:text-white"
        }`}
      >
        NO
      </button>
    </div>
  );
}

function CriteriaGroup({ title, criteria, values, onChange, testPrefix }) {
  return (
    <div className="space-y-2">
      {title && <div className="text-[10px] uppercase tracking-widest text-primary-app font-bold mb-2">{title}</div>}
      {criteria.map((c) => (
        <div key={c.key} className="grid grid-cols-5 gap-3 items-center">
          <div className="col-span-3 text-xs text-white leading-tight">{c.label}</div>
          <div className="col-span-2">
            <YesNoToggle
              value={values?.[c.key]}
              onChange={(v) => onChange({ ...values, [c.key]: v })}
              testid={`${testPrefix}-${c.key}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoreBadge({ value, max = 3 }) {
  let color = "#A3A3A3";
  if (value >= 3) color = "#32D74B";
  else if (value === 2) color = "#FFD60A";
  else if (value === 1) color = "#FF9500";
  else color = "#FF3B30";
  return (
    <div className="flex items-baseline gap-1" title={`Puntaje / ${max}`}>
      <span className="font-display font-black text-3xl" style={{ color }}>{value}</span>
      <span className="text-xs text-muted">/ {max}</span>
    </div>
  );
}

function Section({ title, subtitle, score, children }) {
  return (
    <div className="border border-app bg-[#0A0A0A] p-5 mb-4">
      <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-app">
        <div>
          <div className="font-display font-black uppercase text-lg tracking-tighter">{title}</div>
          {subtitle && <div className="text-xs text-muted mt-0.5">{subtitle}</div>}
        </div>
        <ScoreBadge value={score} />
      </div>
      {children}
    </div>
  );
}

// ============================================================
// Main FMS Form
// ============================================================

export default function FmsFormFull({ data, onChange, sex = "MASCULINO" }) {
  const d = data || {};
  const set = (patch) => onChange({ ...d, ...patch });

  const totals = useMemo(() => computeFmsTotals(d, sex), [d, sex]);

  return (
    <div className="space-y-4" data-testid="fms-form-full">
      <div className="flex items-center justify-between bg-[#141414] border border-[#FF3B30] p-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-primary-app">FMS · Functional Movement Screen</div>
          <div className="font-display font-black uppercase text-2xl tracking-tighter">TOTAL</div>
          <div className="text-[10px] text-muted mt-1">Planilla ISEF N°1 Dr. E. Romero Brest · sexo: {sex}</div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-5xl text-white">{totals.total}<span className="text-2xl text-muted">/21</span></div>
          <div className={`text-[10px] uppercase tracking-widest font-bold mt-1 ${totals.total >= 14 ? "text-[#32D74B]" : totals.total >= 11 ? "text-[#FFD60A]" : "text-[#FF3B30]"}`}>
            {totals.total >= 14 ? "Óptimo" : totals.total >= 11 ? "Aceptable" : "Riesgo"}
          </div>
        </div>
      </div>

      {/* 1. Deep Squat — empuje bilateral simétrico */}
      <Section
        title="1. Empuje bilateral simétrico"
        subtitle="Deep Squat · un solo lado · 6 criterios"
        score={totals.deepSquat}
      >
        <CriteriaGroup
          criteria={DEEP_SQUAT_CRITERIA}
          values={d.deep_squat}
          onChange={(v) => set({ deep_squat: v })}
          testPrefix="fms-ds"
        />
      </Section>

      {/* 2. Hurdle Step — patrón apoyo doble a simple */}
      <Section
        title="2. Patrón apoyo doble a simple"
        subtitle="Paso de valla · izquierdo y derecho · se toma el menor"
        score={totals.hurdle}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Lado Izquierdo</div>
              <ScoreBadge value={totals.hurdleL} />
            </div>
            <CriteriaGroup
              criteria={HURDLE_CRITERIA}
              values={d.hurdle_left}
              onChange={(v) => set({ hurdle_left: v })}
              testPrefix="fms-hurdle-l"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Lado Derecho</div>
              <ScoreBadge value={totals.hurdleR} />
            </div>
            <CriteriaGroup
              criteria={HURDLE_CRITERIA}
              values={d.hurdle_right}
              onChange={(v) => set({ hurdle_right: v })}
              testPrefix="fms-hurdle-r"
            />
          </div>
        </div>
      </Section>

      {/* 3. Inline Lunge */}
      <Section
        title="3. Estocada en línea"
        subtitle="Inline Lunge · izquierdo y derecho · se toma el menor"
        score={totals.lunge}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Lado Izquierdo</div>
              <ScoreBadge value={totals.lungeL} />
            </div>
            <CriteriaGroup
              criteria={LUNGE_CRITERIA}
              values={d.lunge_left}
              onChange={(v) => set({ lunge_left: v })}
              testPrefix="fms-lunge-l"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-muted font-bold">Lado Derecho</div>
              <ScoreBadge value={totals.lungeR} />
            </div>
            <CriteriaGroup
              criteria={LUNGE_CRITERIA}
              values={d.lunge_right}
              onChange={(v) => set({ lunge_right: v })}
              testPrefix="fms-lunge-r"
            />
          </div>
        </div>
      </Section>

      {/* 4. Shoulder Mobility */}
      <Section
        title="4. Movilidad articular del hombro"
        subtitle="Distancia entre ambos puños · se toma el menor de los lados"
        score={totals.shoulder}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <SelectCriterion
            label="Lado Izquierdo"
            value={d.shoulder_left}
            onChange={(v) => set({ shoulder_left: v })}
            options={SHOULDER_DISTANCE}
            score={totals.shoulderL}
            testid="fms-shoulder-l"
          />
          <SelectCriterion
            label="Lado Derecho"
            value={d.shoulder_right}
            onChange={(v) => set({ shoulder_right: v })}
            options={SHOULDER_DISTANCE}
            score={totals.shoulderR}
            testid="fms-shoulder-r"
          />
        </div>
      </Section>

      {/* 5. Active SLR */}
      <Section
        title="5. Elevación activa de MMII"
        subtitle="Active Straight Leg Raise · izquierdo y derecho · se toma el menor"
        score={totals.slr}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <SelectCriterion
            label="Lado Izquierdo"
            value={d.slr_left}
            onChange={(v) => set({ slr_left: v })}
            options={SLR_OPTIONS}
            score={totals.slrL}
            testid="fms-slr-l"
          />
          <SelectCriterion
            label="Lado Derecho"
            value={d.slr_right}
            onChange={(v) => set({ slr_right: v })}
            options={SLR_OPTIONS}
            score={totals.slrR}
            testid="fms-slr-r"
          />
        </div>
      </Section>

      {/* 6. Trunk Stability */}
      <Section
        title="6. Estabilidad de tronco (Push Up)"
        subtitle={`Diferenciado por sexo · calculando para ${sex}`}
        score={totals.trunk}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-5 gap-3 items-center">
            <div className="col-span-3 text-xs text-white">Posición inicial pulgares</div>
            <div className="col-span-2">
              <select
                className="input-base !py-1.5 !text-xs"
                value={d.trunk_stability?.posicion || ""}
                onChange={(e) => set({ trunk_stability: { ...d.trunk_stability, posicion: e.target.value } })}
                data-testid="fms-trunk-position"
              >
                <option value="">— Elegir —</option>
                {sex === "MASCULINO"
                  ? TRUNK_POSITION.map((o) => <option key={o.value} value={o.value}>{o.label} ({o.points})</option>)
                  : <option value="Mentón">Mentón</option>}
                {sex !== "MASCULINO" && <option value="ninguno">Otra posición</option>}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3 items-center">
            <div className="col-span-3 text-xs text-white">Elevación como unidad</div>
            <div className="col-span-2">
              <YesNoToggle
                value={d.trunk_stability?.elevacion_unidad}
                onChange={(v) => set({ trunk_stability: { ...d.trunk_stability, elevacion_unidad: v } })}
                testid="fms-trunk-elevacion"
              />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3 items-center">
            <div className="col-span-3 text-xs text-white">Compensaciones</div>
            <div className="col-span-2">
              <YesNoToggle
                value={d.trunk_stability?.compensaciones}
                onChange={(v) => set({ trunk_stability: { ...d.trunk_stability, compensaciones: v } })}
                testid="fms-trunk-compens"
              />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3 items-center">
            <div className="col-span-3 text-xs text-white">¿Pinzamiento en extensión? <span className="text-[10px] text-[#FF3B30]">(si=0 pts)</span></div>
            <div className="col-span-2">
              <YesNoToggle
                value={d.trunk_stability?.pinzamiento}
                onChange={(v) => set({ trunk_stability: { ...d.trunk_stability, pinzamiento: v } })}
                testid="fms-trunk-pinza"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* 7. Rotary Stability */}
      <Section
        title="7. Estabilidad rotacional"
        subtitle="Rotary Stability · dolor en flexión tronco = 0 puntos"
        score={totals.rotary}
      >
        <div className="space-y-2">
          {ROTARY_CRITERIA.map((c) => (
            <div key={c.key} className="grid grid-cols-5 gap-3 items-center">
              <div className="col-span-3 text-xs text-white">{c.label}</div>
              <div className="col-span-2">
                <YesNoToggle
                  value={d.rotary?.[c.key]}
                  onChange={(v) => set({ rotary: { ...d.rotary, [c.key]: v } })}
                  testid={`fms-rotary-${c.key}`}
                />
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function SelectCriterion({ label, value, onChange, options, score, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-muted font-bold">{label}</div>
        <ScoreBadge value={score} />
      </div>
      <select
        className="input-base !py-2 !text-xs"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        <option value="">— Elegir —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label} ({o.points} pts)</option>)}
      </select>
    </div>
  );
}
